import { UseMutationOptions } from '@tanstack/react-query';
import { AxiosResponse } from 'axios';
import { useAppMutation } from './useAppMutation';

/**
 * Queues the Course Progress Report — one row per learner, one column per course — and emails it to the
 * requesting training manager.
 *
 * Takes the SAME filters as the students export beside it on the menu, so the two always describe the
 * same people and the same courses. Every filter is optional: with no course selection the report
 * covers every course the filtered students hold an enrolment for.
 */
interface ExportCourseProgressParams {
  courseIds?: string[];
  facultyIds?: string[];
  // The student filters too. They scope the ROWS; omitting them exported the manager's whole roster
  // while the screen showed a narrowed list.
  searchText?: string;
  statuses?: string[];
  providerId?: string;
  enrolmentDateFrom?: string;
  enrolmentDateTo?: string;
  completedDateFrom?: string;
  completedDateTo?: string;
  dueDateFrom?: string;
  dueDateTo?: string;
  expiryDateFrom?: string;
  expiryDateTo?: string;
}

export const useExportCourseProgressReport = () => {
  const { mutate, isPending } = useAppMutation<AxiosResponse<{ queued: boolean }>, Error, any>(
    'get',
    '/TrainingManager/courses/export-course-progress'
  );

  const exportCourseProgress = (
    params: ExportCourseProgressParams,
    options: UseMutationOptions
  ) => {
    mutate(
      {
        params: {
          courseIds: params.courseIds?.length ? params.courseIds : undefined,
          facultyIds: params.facultyIds?.length ? params.facultyIds : undefined,
          searchText: params.searchText || undefined,
          statuses: params.statuses?.length ? params.statuses : undefined,
          providerId: params.providerId,
          enrolmentDateFrom: params.enrolmentDateFrom,
          enrolmentDateTo: params.enrolmentDateTo,
          completedDateFrom: params.completedDateFrom,
          completedDateTo: params.completedDateTo,
          dueDateFrom: params.dueDateFrom,
          dueDateTo: params.dueDateTo,
          expiryDateFrom: params.expiryDateFrom,
          expiryDateTo: params.expiryDateTo,
        },
        // indexes: null produces repeated `courseIds=a&courseIds=b`, which is what ASP.NET binds to
        // List<T>. The default bracketed form binds as a single string and silently filters nothing.
        paramsSerializer: { indexes: null },
      },
      {
        ...options,
      }
    );
  };

  return { exportCourseProgress, isPending };
};
